/**
 * PAY-1 · 官网收款（OP 初筛 + 财务查账）+ 汇率 + 客服与收款码 · 服务层。
 * 真契约源：apps/api-server/src/api_server/routes/site_payments.py（API_MAP 见 designs/mockups/site-payments/API_MAP.md）。
 * 前端只读接口返回的数（阶段、等待时长、是否超时、金额），不做任何业务推算（铁律十三）。
 */
import { api, apiConfig, tryRefresh } from "./api";
import { useAuthStore } from "@/store/authStore";

export type PaymentStage = "screen" | "audit" | "exception" | "done";

export interface SitePaymentRow {
  id: string;
  order_no: string;
  submitted_at: string;
  stage: "screen" | "audit" | "exception" | "done" | "rejected";
  product: string;
  product_code: string | null;
  travel_date: string | null;
  pax: number | null;
  customer: string;
  phone: string;
  cny_total_fen: number | null;
  usd_total_cents: number | null;
  payment_method: "wechat" | "alipay" | string | null;
  payment_ref: string | null;
  needs_reschedule_or_refund: boolean;
  order_status: string;
  screened_by: string | null;
  screened_at: string | null;
  audited_by: string | null;
  audited_at: string | null;
  note: string | null;
  reject_reason: string | null;
  /** 仅 stage=screen */
  waited_minutes?: number;
  overdue?: boolean;
  /** 仅 stage=audit / exception */
  days_left?: number;
  urgent?: boolean;
}

export interface FxCurrent { rate: string; effective_at: string; set_by: string | null }
export interface FxOverview { current: FxCurrent | null; history: FxCurrent[] }

export interface SitePaymentList {
  stage: PaymentStage;
  counts: Record<PaymentStage, number>;
  items: SitePaymentRow[];
  fx: FxOverview;
}

export interface SitePaymentDetail extends SitePaymentRow {
  wechat: string | null;
  fx_rate: string | null;
  rooms_note: string;
  proof: { url: string; content_type: string; size_bytes: number } | null;
  reject_reasons: { code: string; label: string }[];
}

export interface ScreenPassResult {
  order_no: string;
  order_status: string;
  confirmed: boolean;
  voucher: { issued?: boolean; sent?: boolean; [k: string]: unknown } | null;
  message?: string;
}

export interface SiteContact {
  contact_wechat: string;
  contact_phone: string;
  contact_wechat_qr_url: string | null;
  service_hours: string;
  business_license: string;
}

export interface PaymentQrInfo { url: string | null; label: string; last_changed_by: string | null; last_changed_at: string | null }
export interface ZelleInfo { account: string | null; label: string; last_changed_by: string | null; last_changed_at: string | null }
/** MANUAL-ORD-1（用户 10-29）· zelle = Zelle 收款账号（邮箱 / 手机号文字，不是收款码）；手工散客付款页 /pay/{token} 显示 */
export type PaymentQrOverview = Record<"wechat" | "alipay", PaymentQrInfo> & { zelle?: ZelleInfo };
export interface PaymentQrHistoryRow { old_url: string | null; new_url: string; changed_by: string | null; changed_at: string }

const BASE = "/api/v1/site-payments";
const json = (body: unknown) => JSON.stringify(body);

export const sitePaymentsApi = {
  list: (stage: PaymentStage, limit = 200) => api<SitePaymentList>(`${BASE}?stage=${stage}&limit=${limit}`, { paged: true }),
  /** 侧栏数字：只要 counts，不拉明细 */
  counts: () => api<SitePaymentList>(`${BASE}?stage=screen&limit=1`, { paged: true }).then((r) => r.counts),
  detail: (id: string) => api<SitePaymentDetail>(`${BASE}/${id}`),
  screenPass: (id: string) => api<ScreenPassResult>(`${BASE}/${id}:screen-pass`, { method: "POST" }),
  screenReject: (id: string, reason_code: string, note?: string) =>
    api<{ order_no: string; reject_reason: string }>(`${BASE}/${id}:screen-reject`, { method: "POST", body: json({ reason_code, note: note ?? null }) }),
  markReceived: (ids: string[]) =>
    api<{ done: number; skipped: string[]; total_cny_fen: number }>(`${BASE}:mark-received`, { method: "POST", body: json({ ids }) }),
  markMissing: (id: string, note: string) => api<{ order_no: string; note: string }>(`${BASE}/${id}:mark-missing`, { method: "POST", body: json({ note }) }),
  recheck: (id: string) => api<{ order_no: string }>(`${BASE}/${id}:recheck`, { method: "POST" }),

  fx: () => api<FxOverview>("/api/v1/finance/fx-rate"),
  setFx: (rate: string, confirm_rate: string) => api<FxOverview>("/api/v1/finance/fx-rate", { method: "PUT", body: json({ rate, confirm_rate }) }),

  contact: () => api<SiteContact>("/api/v1/settings/site-contact"),
  patchContact: (p: Partial<Pick<SiteContact, "contact_wechat" | "contact_phone" | "business_license">>) =>
    api<SiteContact>("/api/v1/settings/site-contact", { method: "PATCH", body: json(p) }),
  putContactQr: (file: File) => putForm<SiteContact>("/api/v1/settings/site-contact/wechat-qr", file),

  paymentQr: () => api<PaymentQrOverview>("/api/v1/settings/payment-qr"),
  paymentQrHistory: (method: "wechat" | "alipay" | "zelle") => api<PaymentQrHistoryRow[]>(`/api/v1/settings/payment-qr/${method}/history`),
  putZelle: (account: string, confirmCompanyAccount: boolean) =>
    api<PaymentQrOverview>("/api/v1/settings/payment-zelle", { method: "PUT", body: json({ account, confirm_company_account: confirmCompanyAccount }) }),
  putPaymentQr: (method: "wechat" | "alipay", file: File, confirmCompanyAccount: boolean) =>
    putForm<PaymentQrOverview>(`/api/v1/settings/payment-qr/${method}`, file, { confirm_company_account: confirmCompanyAccount ? "true" : "false" }),
};

/** multipart PUT（api() 固定 JSON 头，所以和 uploadReceipt 一样单独写；401 刷新后重试一次）。 */
async function putForm<T>(path: string, file: File, fields: Record<string, string> = {}): Promise<T> {
  const doPut = () => {
    const token = useAuthStore.getState().accessToken;
    const tenantSlug = useAuthStore.getState().user?.tenantSlug;
    const fd = new FormData();
    fd.append("file", file, file.name || "qr.png");
    for (const [k, v] of Object.entries(fields)) fd.append(k, v);
    return fetch(`${apiConfig.base}${path}`, {
      method: "PUT",
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(tenantSlug ? { "X-Tenant-Slug": tenantSlug } : {}) },
      body: fd,
    });
  };
  let res = await doPut();
  if (res.status === 401 && useAuthStore.getState().refreshToken && (await tryRefresh())) res = await doPut();
  if (!res.ok) {
    let msg = `上传失败（${res.status}）`;
    try { const b = await res.json(); if (typeof b?.detail === "string") msg = b.detail; } catch { /* 非 JSON */ }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

// ───────────────────────── 展示小工具（只格式化，不推算） ─────────────────────────

export const cny = (fen: number | null | undefined) => (fen == null ? "—" : `¥${(fen / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`);
export const usd = (cents: number | null | undefined) => (cents == null ? "" : `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`);
export const METHOD_CN: Record<string, string> = { wechat: "微信", alipay: "支付宝" };

/** 公司运营时区（太平洋）MM/DD HH:mm */
export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const p = new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false })
    .formatToParts(new Date(iso));
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? "";
  return `${g("month")}/${g("day")} ${g("hour") === "24" ? "00" : g("hour")}:${g("minute")}`;
}
export const fmtMD = (ymd: string | null | undefined) => (ymd ? `${ymd.slice(5, 7)}/${ymd.slice(8, 10)}` : "—");
export function waitedText(min: number): string {
  const h = Math.floor(min / 60), m = min % 60;
  return h > 0 ? `已等 ${h} 小时${m ? ` ${m} 分` : ""}` : `已等 ${m} 分钟`;
}
